// src/pages/QuoteRequestDetail.js - Refactored with extracted hooks

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { createQuoteLineVersion } from '../api';
import {
  QuoteHeader,
  ProgressSection,
  ActionToolbar,
  QuoteLineRow,
  BulkActionsToolbar,
  TemplatesModal,
  NotificationToast
} from './QuoteRequestDetail/components';
import QuoteMessaging from '../components/QuoteMessaging';
import QuoteActions from '../components/QuoteActions';
import RemarksModal from '../components/RemarksModal';
import CertificateModal from '../components/CertificateModal';
import VersionHistoryModal from '../components/VersionHistoryModal';
import useModalBehavior from '../hooks/useModalBehavior';
import './QuoteRequestDetail/styles/index.css';
import '../components/EditableCell.css';

// Import utilities
import {
  getFieldLabel,
  getCellValue,
  getRowDescription,
  getCertificationIdByName,
  templates
} from './QuoteRequestDetail/utils';

// ────────────────────────────────────────────────────────────────────────────
// Pagination helpers (module scope - pure, no component state)
// ────────────────────────────────────────────────────────────────────────────

const PAGINATION_THRESHOLD = 25;
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 'all'];

// Paginates over ORIGINAL lines only (alternatives always ride along with
// their parent original - see callers, which expand pageIds to include an
// alternative whenever its parentId is in pageIds).
function paginateOriginals(originalsOnly, currentPage, pageSize) {
  const total = originalsOnly.length;
  if (total <= PAGINATION_THRESHOLD) {
    return {
      paginated: false,
      total,
      totalPages: 1,
      page: 1,
      effectivePageSize: total,
      startIdx: 0,
      endIdx: total,
      pageIds: new Set(originalsOnly.map(l => l.id)),
    };
  }
  const effectivePageSize = pageSize === 'all' ? total : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const page = Math.min(Math.max(1, currentPage), totalPages);
  const startIdx = (page - 1) * effectivePageSize;
  const endIdx = Math.min(startIdx + effectivePageSize, total);
  return {
    paginated: true,
    total,
    totalPages,
    page,
    effectivePageSize,
    startIdx,
    endIdx,
    pageIds: new Set(originalsOnly.slice(startIdx, endIdx).map(l => l.id)),
  };
}

// Windowed page-number list for the pagination bar: all pages when there
// are few, otherwise first/last + a window around the current page with
// '…' gaps (returned as the literal string so callers can render it as a
// non-interactive spacer).
function getPageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const keep = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
  const sorted = Array.from(keep).filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  let prev = null;
  sorted.forEach(p => {
    if (prev !== null && p - prev > 1) result.push('…');
    result.push(p);
    prev = p;
  });
  return result;
}

export default function QuoteRequestDetail() {
  const { id } = useParams();

  // ────────────────────────────────────────────────────────────────────────────
  // Core Data State
  // ────────────────────────────────────────────────────────────────────────────

  const [request, setRequest] = useState(null);
  const [lines, setLines] = useState([]);
  const [expandedOriginals, setExpandedOriginals] = useState(new Set());
  const [showAlternativesUI, setShowAlternativesUI] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination (client-side, over original lines only - see paginateOriginals)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Field options
  const [conditionOptions, setConditionOptions] = useState([]);
  const [certificationOptions, setCertificationOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [uomOptions, setUomOptions] = useState([]);

  // ────────────────────────────────────────────────────────────────────────────
  // Editing State
  // ────────────────────────────────────────────────────────────────────────────

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [focusedCell, setFocusedCell] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  // ────────────────────────────────────────────────────────────────────────────
  // Bulk Operations State
  // ────────────────────────────────────────────────────────────────────────────

  const [selectedLines, setSelectedLines] = useState(new Set());
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkValues, setBulkValues] = useState({
    condition: '',
    certification_id: '',
    lead_time: '',
    vendor_id: ''
  });
  const [copiedData, setCopiedData] = useState(null);

  // ────────────────────────────────────────────────────────────────────────────
  // Modal State
  // ────────────────────────────────────────────────────────────────────────────

  const [showTemplates, setShowTemplates] = useState(false);
  const [templateTargetLineId, setTemplateTargetLineId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [showMessaging, setShowMessaging] = useState(false);
  const [selectedLineForMessaging, setSelectedLineForMessaging] = useState(null);
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [selectedLineForRemarks, setSelectedLineForRemarks] = useState(null);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedLineForCertificate, setSelectedLineForCertificate] = useState(null);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  const [selectedLineForVersion, setSelectedLineForVersion] = useState(null);

  // ────────────────────────────────────────────────────────────────────────────
  // Refs
  // ────────────────────────────────────────────────────────────────────────────

  const editInputRef = useRef(null);
  const tableRef = useRef(null);
  const screenReaderRef = useRef(null);
  const announceTimeoutRef = useRef(null);
  const requestMessagingRef = useRef(null);
  const lineMessagingRef = useRef(null);

  const handleCloseMessaging = useCallback(() => {
    setShowMessaging(false);
  }, []);

  const messagingPanelRef = useModalBehavior({ isOpen: showMessaging, onClose: handleCloseMessaging });

  // ────────────────────────────────────────────────────────────────────────────
  // Accessibility Helpers
  // ────────────────────────────────────────────────────────────────────────────

  const announceToScreenReader = useCallback((message) => {
    if (announceTimeoutRef.current) {
      clearTimeout(announceTimeoutRef.current);
      announceTimeoutRef.current = null;
    }
    if (screenReaderRef.current) {
      screenReaderRef.current.textContent = message;
    }
    announceTimeoutRef.current = setTimeout(() => {
      if (screenReaderRef.current) {
        screenReaderRef.current.textContent = '';
      }
    }, 1000);
  }, []);

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // ────────────────────────────────────────────────────────────────────────────
  // Data Transformation
  // ────────────────────────────────────────────────────────────────────────────

  const transformLinesToNested = useCallback((flatLines) => {
    const originals = flatLines.filter(line => !line.is_alt);
    const alternatives = flatLines.filter(line => line.is_alt);
    const originalToAlternatives = new Map();
    const originalByProductId = new Map();
    originals.forEach(orig => {
      if (!originalByProductId.has(orig.product_id)) {
        originalByProductId.set(orig.product_id, orig);
      }
    });

    alternatives.forEach(alt => {
      if (alt.alt_for_product_id) {
        const originalLine = originalByProductId.get(alt.alt_for_product_id);
        if (originalLine) {
          const key = originalLine.id;
          if (!originalToAlternatives.has(key)) {
            originalToAlternatives.set(key, []);
          }
          originalToAlternatives.get(key).push(alt);
        } else {
          console.warn(`Orphaned alternative found: line ${alt.id} references product ${alt.alt_for_product_id} not in current quote`);
          originals.push({ ...alt, is_orphaned_alt: true });
        }
      } else {
        console.warn(`Alternative without product reference: line ${alt.id}`);
        originals.push({ ...alt, is_orphaned_alt: true });
      }
    });

    const displayLines = [];
    originals.forEach(original => {
      displayLines.push({ ...original, type: 'original' });
      if (expandedOriginals.has(original.id) && originalToAlternatives.has(original.id)) {
        const alts = originalToAlternatives.get(original.id);
        alts.forEach((alt, index) => {
          displayLines.push({
            ...alt,
            type: 'alternative',
            parentId: original.id,
            isFirstAlternative: index === 0,
            isLastAlternative: index === alts.length - 1
          });
        });
      }
    });

    return { displayLines, originalToAlternatives, hasAlternatives: alternatives.length > 0 };
  }, [expandedOriginals]);

  // ────────────────────────────────────────────────────────────────────────────
  // Data Fetching
  // ────────────────────────────────────────────────────────────────────────────

  const loadFieldOptions = useCallback(async () => {
    try {
      const { data } = await api.get('/api/quote-lines/options');
      if (data.success) {
        setConditionOptions(data.options.conditions.map(o => o.value));
        setCertificationOptions(data.options.certifications);
        setCurrencyOptions(data.options.currencies || []);
        setUomOptions(data.options.uoms || []);
      }
    } catch (err) {
      console.error('Error loading options:', err);
      setConditionOptions(['FN', 'NE', 'NS', 'OH', 'SV', 'AR']);
      setCertificationOptions([]);
      setUomOptions([]);
    }
  }, []);

  const fetchQuoteDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reqRes = await api.get(`/api/quote-requests/${id}`);
      if (reqRes.data.success) {
        setRequest(reqRes.data.request);
      }
      const linesRes = await api.get(`/api/quote-requests/${id}/lines`);
      if (linesRes.data.success) {
        const flatLines = linesRes.data.lines;
        setLines(flatLines);
        const hasAlternatives = flatLines.some(l => l.is_alt);
        setShowAlternativesUI(hasAlternatives);
        announceToScreenReader(`Quote loaded with ${flatLines.length} line items`);
      }
    } catch (err) {
      console.error('Error fetching details:', err);
      setError('Failed to load quote data. Please try again.');
      announceToScreenReader('Error loading quote data');
    } finally {
      setLoading(false);
    }
  }, [id, announceToScreenReader]);

  useEffect(() => {
    fetchQuoteDetails();
    loadFieldOptions();
  }, [id, fetchQuoteDetails, loadFieldOptions]);

  // ────────────────────────────────────────────────────────────────────────────
  // Message Notification Listener
  // ────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const handleMessageNotification = (event) => {
      console.log('💬 Message push notification received in QuoteRequestDetail:', event.detail);
      const { requestId: notifRequestId, lineId: notifLineId } = event.detail || {};

      if (notifRequestId && parseInt(notifRequestId) === parseInt(id)) {
        console.log('🔄 Refreshing messages for current request');
        if (notifLineId && lineMessagingRef.current && selectedLineForMessaging?.id === parseInt(notifLineId)) {
          console.log('🔄 Refreshing line messages');
          lineMessagingRef.current.refresh();
        } else if (!notifLineId && requestMessagingRef.current && !selectedLineForMessaging) {
          console.log('🔄 Refreshing request messages');
          requestMessagingRef.current.refresh();
        }
        if (!showMessaging) {
          console.log('💡 Messaging panel is closed, new message available');
        }
      }
    };

    window.addEventListener('messageNotification', handleMessageNotification);
    return () => window.removeEventListener('messageNotification', handleMessageNotification);
  }, [id, selectedLineForMessaging, showMessaging]);

  // ────────────────────────────────────────────────────────────────────────────
  // Focus Effect
  // ────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!focusedCell) return;
    const { lineIndex, field } = focusedCell;
    requestAnimationFrame(() => {
      document.querySelectorAll('.cell-focused').forEach(el => el.classList.remove('cell-focused'));
      const cell = document.getElementById(`cell-${lineIndex}-${field}`);
      if (cell) {
        cell.classList.add('cell-focused');
        cell.focus();
        cell.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }, [focusedCell]);

  // ────────────────────────────────────────────────────────────────────────────
  // Pagination Reset
  // ────────────────────────────────────────────────────────────────────────────

  // Reset to page 1 when switching quotes or changing page size. Deliberately
  // does NOT depend on `lines` so inline edits/saves never bounce the page.
  useEffect(() => {
    setCurrentPage(1);
  }, [id, pageSize]);

  // ────────────────────────────────────────────────────────────────────────────
  // Validation
  // ────────────────────────────────────────────────────────────────────────────

  const validateField = useCallback((field, value) => {
    if (field === 'price_unit' && (isNaN(value) || parseFloat(value) <= 0)) {
      return 'Price must be a positive number';
    }
    if (field === 'lead_time' && (isNaN(value) || parseInt(value) < 0)) {
      return 'Lead time must be non-negative';
    }
    if (field === 'condition' && value && conditionOptions.length > 0 && !conditionOptions.includes(value)) {
      return 'Invalid condition';
    }
    if (field === 'certification_id' && value && certificationOptions.length > 0) {
      const validIds = certificationOptions.map(c => c.value);
      if (!validIds.includes(value)) {
        return 'Invalid certification';
      }
    }
    return null;
  }, [conditionOptions, certificationOptions]);

  // ────────────────────────────────────────────────────────────────────────────
  // Editing Helpers
  // ────────────────────────────────────────────────────────────────────────────

  const cancelEditing = useCallback(() => {
    if (editingCell) {
      announceToScreenReader(`Cancelled editing ${getFieldLabel(editingCell.field)}`);
    }
    setEditingCell(null);
    setEditValue('');
    setIsDirty(false);
  }, [editingCell, announceToScreenReader]);

  const autoUpdateToInProgress = useCallback(async (lineId) => {
    const ln = lines.find(l => l.id === lineId);
    if (ln && ln.state === 'draft') {
      try {
        const res = await api.post(`/api/quote-lines/${lineId}/start`);
        if (res.data.success) {
          setLines(prev => prev.map(l =>
            l.id === lineId
              ? { ...l, state: res.data.state, state_display: res.data.state_display }
              : l
          ));
        }
      } catch (err) {
        console.error('Auto-update error:', err);
      }
    }
  }, [lines]);

  // ────────────────────────────────────────────────────────────────────────────
  // Bulk Actions
  // ────────────────────────────────────────────────────────────────────────────

  const clearSelection = useCallback(() => {
    const count = selectedLines.size;
    setSelectedLines(new Set());
    setBulkEditMode(false);
    announceToScreenReader(`Cleared selection of ${count} lines`);
  }, [selectedLines.size, announceToScreenReader]);

  // Selects every incomplete line on the CURRENT PAGE when the quote is
  // paginated (>25 original lines), otherwise every incomplete line in the
  // whole quote. Recomputes the page grouping itself (via transformLinesToNested
  // + paginateOriginals) rather than depending on the render-only displayLines
  // memo, so it stays valid regardless of hook declaration order.
  const selectAllLines = useCallback(() => {
    const { displayLines: allDisplay } = transformLinesToNested(lines);
    const originalsOnly = allDisplay.filter(l => l.type === 'original');
    const { paginated, pageIds } = paginateOriginals(originalsOnly, currentPage, pageSize);

    const scopeIds = paginated
      ? new Set(
          allDisplay
            .filter(l => (l.type === 'original' ? pageIds.has(l.id) : pageIds.has(l.parentId)))
            .map(l => l.id)
        )
      : null;

    const allIds = new Set(
      lines
        .filter(l => (scopeIds ? scopeIds.has(l.id) : true) && l.state !== 'completed')
        .map(l => l.id)
    );
    setSelectedLines(allIds);
    showNotification(`Selected ${allIds.size} line${allIds.size === 1 ? '' : 's'}${paginated ? ' on this page' : ''}`, 'info');
    announceToScreenReader(`Selected ${allIds.size} incomplete lines${paginated ? ' on this page' : ''}`);
  }, [lines, transformLinesToNested, currentPage, pageSize, showNotification, announceToScreenReader]);

  const copyLineData = useCallback((line) => {
    setCopiedData({
      condition: line.condition,
      certification_id: line.certification_id,
      certification_name: line.certification_name,
      lead_time: line.lead_time,
      price_unit: line.price_unit,
      vendor_id: line.vendor_id,
      vendor_name: line.vendor_name
    });
    showNotification('Line data copied', 'info');
    announceToScreenReader(`Copied data from part ${line.name}`);
  }, [showNotification, announceToScreenReader]);

  const pasteToLine = useCallback(async (lineId) => {
    if (!copiedData) {
      announceToScreenReader('No data to paste');
      return showNotification('No data to paste', 'warning');
    }
    setSaving(true);
    try {
      const res = await api.post(`/api/quote-lines/${lineId}`, copiedData);
      if (res.data.success) {
        setLines(prev => prev.map(l => l.id === lineId ? { ...l, ...res.data.line } : l));
        showNotification('Data pasted successfully', 'success');
        const line = lines.find(l => l.id === lineId);
        announceToScreenReader(`Pasted data to part ${line?.name || 'line'}`);
      }
    } catch (err) {
      console.error('Paste error:', err);
      showNotification('Failed to paste data', 'error');
      announceToScreenReader('Failed to paste data');
    } finally {
      setSaving(false);
    }
  }, [copiedData, lines, showNotification, announceToScreenReader]);

  const duplicateLineData = useCallback(async (line) => {
    setSaving(true);
    try {
      const res = await api.post(`/api/quote-lines/${line.id}/duplicate`, {});
      if (res.data.success) {
        setLines(prevLines => [...prevLines, res.data.line]);
        showNotification(res.data.message || 'Line duplicated successfully', 'success');
        announceToScreenReader(`Duplicated line for part ${line.name}`);
      } else {
        showNotification('Failed to duplicate line', 'error');
        announceToScreenReader('Failed to duplicate line');
      }
    } catch (err) {
      console.error('Duplicate line error:', err);
      showNotification('Error duplicating line', 'error');
      announceToScreenReader('Error duplicating line');
    } finally {
      setSaving(false);
    }
  }, [showNotification, announceToScreenReader]);

  // ────────────────────────────────────────────────────────────────────────────
  // Quick Update & Complete
  // ────────────────────────────────────────────────────────────────────────────

  const quickUpdateField = useCallback(async (lineId, field, value) => {
    setSaving(true);
    try {
      const validationError = validateField(field, value);
      if (validationError) {
        console.error(`Validation failed for ${field}:`, validationError);
        showNotification(`Cannot save: ${validationError}`, 'error');
        announceToScreenReader(`Cannot save: ${validationError}`);
        setSaving(false);
        return;
      }

      const line = lines.find(l => l.id === lineId);
      const payload = { [field]: value };
      if ((field === 'certification_id' || field === 'condition') && line?.product_id) {
        payload.product_id = line.product_id;
      }

      const res = await api.post(`/api/quote-lines/${lineId}`, payload);
      if (res.data.success) {
        const preservedVendorInfo = line?.vendor_id ? {
          vendor_id: line.vendor_id,
          vendor_name: line.vendor_name
        } : {};

        setLines(prev => prev.map(l => l.id === lineId ? {
          ...l,
          ...res.data.line,
          ...preservedVendorInfo
        } : l));

        const message = `${getFieldLabel(field)} updated to ${value} for part ${line?.name}`;
        showNotification(message, 'success');
        announceToScreenReader(message);
      } else {
        showNotification(res.data.error || 'Failed to update', 'error');
        announceToScreenReader(res.data.error || `Failed to update ${getFieldLabel(field)}`);
      }
    } catch (err) {
      console.error('Quick update error:', err);
      showNotification(err.response?.data?.error || `Failed to update ${getFieldLabel(field)}`, 'error');
      announceToScreenReader(`Failed to update ${getFieldLabel(field)}`);
    } finally {
      setSaving(false);
    }
  }, [lines, validateField, showNotification, announceToScreenReader]);

  const completeLine = useCallback(async (lineId) => {
    setSaving(true);
    try {
      const res = await api.post(`/api/quote-lines/${lineId}/complete`);
      if (res.data.success) {
        setLines(prev => prev.map(l =>
          l.id === lineId
            ? { ...l, state: res.data.state, state_display: res.data.state_display }
            : l
        ));
        const line = lines.find(l => l.id === lineId);
        const message = `Line completed successfully for part ${line?.name}`;
        showNotification(message, 'success');
        announceToScreenReader(message);
      } else {
        showNotification(res.data.error || 'Failed to complete line', 'error');
        announceToScreenReader('Failed to complete line');
      }
    } catch (err) {
      console.error('Complete error:', err);
      if (err.response?.data?.error) {
        showNotification(err.response.data.error, 'error');
        announceToScreenReader(err.response.data.error);
      } else {
        showNotification('Error completing line', 'error');
        announceToScreenReader('Error completing line');
      }
    } finally {
      setSaving(false);
    }
  }, [lines, showNotification, announceToScreenReader]);

  // Search for NO QUOTE vendor and cache it
  const findNoQuoteVendor = useCallback(async () => {
    try {
      const response = await api.get(`/api/vendors/search?term=${encodeURIComponent('NO QUOTE')}`);
      const vendors = response.data.vendors || response.data || [];
      // Find exact match for "NO QUOTE" vendor
      const noQuoteVendor = vendors.find(v =>
        v.name?.toUpperCase() === 'NO QUOTE' ||
        v.vendor_name?.toUpperCase() === 'NO QUOTE'
      );
      return noQuoteVendor;
    } catch (err) {
      console.error('Error searching for NO QUOTE vendor:', err);
      return null;
    }
  }, []);

  const noQuoteLine = useCallback(async (lineId) => {
    setSaving(true);
    try {
      // First, find the NO QUOTE vendor
      const noQuoteVendor = await findNoQuoteVendor();

      if (!noQuoteVendor) {
        showNotification('Could not find "NO QUOTE" vendor. Please create it first.', 'error');
        announceToScreenReader('NO QUOTE vendor not found');
        setSaving(false);
        return;
      }

      const vendorId = noQuoteVendor.id || noQuoteVendor.vendor_id;
      const vendorName = noQuoteVendor.name || noQuoteVendor.vendor_name || 'NO QUOTE';

      // Update the line with NO QUOTE vendor and price 0
      const res = await api.post(`/api/quote-lines/${lineId}`, {
        vendor_id: vendorId,
        price_unit: 0
      });

      if (res.data.success) {
        const line = lines.find(l => l.id === lineId);

        // Apply the vendor/price update immediately so the UI reflects the
        // server state even if the subsequent /complete call fails.
        setLines(prev => prev.map(l =>
          l.id === lineId
            ? {
                ...l,
                ...res.data.line,
                vendor_id: vendorId,
                vendor_name: vendorName,
                price_unit: 0
              }
            : l
        ));

        // Now complete the line
        const completeRes = await api.post(`/api/quote-lines/${lineId}/complete`);

        if (completeRes.data.success) {
          setLines(prev => prev.map(l =>
            l.id === lineId
              ? {
                  ...l,
                  state: completeRes.data.state,
                  state_display: completeRes.data.state_display
                }
              : l
          ));

          const message = `Line marked as No Quote and completed for part ${line?.name}`;
          showNotification(message, 'success');
          announceToScreenReader(message);
        } else {
          const message = 'Marked as No Quote but could not complete the line';
          showNotification(completeRes.data.error || message, 'warning');
          announceToScreenReader(message);
        }
      } else {
        showNotification(res.data.error || 'Failed to mark as No Quote', 'error');
        announceToScreenReader('Failed to mark as No Quote');
      }
    } catch (err) {
      console.error('No Quote error:', err);
      if (err.response?.data?.error) {
        showNotification(err.response.data.error, 'error');
        announceToScreenReader(err.response.data.error);
      } else {
        showNotification('Error marking as No Quote', 'error');
        announceToScreenReader('Error marking as No Quote');
      }
    } finally {
      setSaving(false);
    }
  }, [lines, showNotification, announceToScreenReader, findNoQuoteVendor]);

  // Bulk No Quote - mark multiple selected lines as No Quote and complete them
  const bulkNoQuote = useCallback(async () => {
    if (!selectedLines.size) {
      showNotification('No lines selected', 'warning');
      return;
    }

    setSaving(true);
    try {
      // First, find the NO QUOTE vendor
      const noQuoteVendor = await findNoQuoteVendor();

      if (!noQuoteVendor) {
        showNotification('Could not find "NO QUOTE" vendor. Please create it first.', 'error');
        announceToScreenReader('NO QUOTE vendor not found');
        setSaving(false);
        return;
      }

      const vendorId = noQuoteVendor.id || noQuoteVendor.vendor_id;
      const vendorName = noQuoteVendor.name || noQuoteVendor.vendor_name || 'NO QUOTE';

      // Use bulk update API to set vendor and price
      const res = await api.post('/api/quote-lines/bulk-update', {
        line_ids: Array.from(selectedLines),
        changes: {
          vendor_id: vendorId,
          price_unit: 0
        }
      });

      if (res.data.success) {
        // Now complete each line
        const lineIds = Array.from(selectedLines);
        const completeResults = await Promise.allSettled(
          lineIds.map(lineId => api.post(`/api/quote-lines/${lineId}/complete`))
        );

        // Build a map of completed line states
        const completedStates = {};
        completeResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value.data.success) {
            completedStates[lineIds[index]] = {
              state: result.value.data.state,
              state_display: result.value.data.state_display
            };
          }
        });

        setLines(prev =>
          prev.map(l => {
            if (selectedLines.has(l.id)) {
              const updated = res.data.lines?.find(u => u.id === l.id);
              const completedState = completedStates[l.id] || {};
              return {
                ...l,
                ...updated,
                vendor_id: vendorId,
                vendor_name: vendorName,
                price_unit: 0,
                ...completedState
              };
            }
            return l;
          })
        );

        const completedCount = Object.keys(completedStates).length;
        const message = `${selectedLines.size} lines marked as No Quote and ${completedCount} completed`;
        showNotification(message, 'success');
        announceToScreenReader(message);
        clearSelection();
      } else {
        showNotification(res.data.error || 'Failed to mark lines as No Quote', 'error');
        announceToScreenReader('Failed to mark lines as No Quote');
      }
    } catch (err) {
      console.error('Bulk No Quote error:', err);
      showNotification('Error marking lines as No Quote', 'error');
      announceToScreenReader('Error marking lines as No Quote');
    } finally {
      setSaving(false);
    }
  }, [selectedLines, showNotification, announceToScreenReader, findNoQuoteVendor, clearSelection]);

  // ────────────────────────────────────────────────────────────────────────────
  // Auto-apply Certification
  // ────────────────────────────────────────────────────────────────────────────

  const autoApplyCertification = useCallback((lineId, condition) => {
    let certName = '';
    if (['FN', 'NE', 'NS'].includes(condition)) certName = 'OEM';
    if (['OH', 'SV'].includes(condition)) certName = '8130';
    if (['AR', 'RP'].includes(condition)) certName = 'EASA';
    if (condition === 'DR') certName = 'Dual Release';
    if (!certName) return;

    const certId = getCertificationIdByName(certificationOptions, certName);
    if (certId) {
      setTimeout(() => quickUpdateField(lineId, 'certification_id', certId), 100);
    }
  }, [certificationOptions, quickUpdateField]);

  // ────────────────────────────────────────────────────────────────────────────
  // Row Event Handlers
  // ────────────────────────────────────────────────────────────────────────────

  const handleLineSelectionChange = useCallback((newSelectedLines) => {
    setSelectedLines(newSelectedLines);
  }, []);

  const handleVendorCancel = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  const handleShowTemplates = useCallback((lineId) => {
    setTemplateTargetLineId(lineId);
    setShowTemplates(true);
  }, []);

  const handleViewLineMessages = useCallback((line) => {
    setSelectedLineForMessaging(line);
    setShowMessaging(true);
    announceToScreenReader(`Viewing messages for line ${line.part_number}`);
  }, [announceToScreenReader]);

  const handleAddRemarks = useCallback((line) => {
    setSelectedLineForRemarks(line);
    setShowRemarksModal(true);
    announceToScreenReader(`Adding remarks for line ${line.part_number || line.name}`);
  }, [announceToScreenReader]);

  const handleUploadCertificate = useCallback((line) => {
    setSelectedLineForCertificate(line);
    setShowCertificateModal(true);
    announceToScreenReader(`Uploading certificate for line ${line.part_number || line.name}`);
  }, [announceToScreenReader]);

  const handleCreateVersion = useCallback(async (lineId) => {
    try {
      const response = await createQuoteLineVersion(lineId);
      if (response.data.success) {
        const newVersion = response.data.data.new_version;
        showNotification(`Version ${newVersion.version_number} created successfully!`, 'success');
        announceToScreenReader(`Created version ${newVersion.version_number} for line`);
        fetchQuoteDetails();
      } else {
        showNotification(response.data.error || 'Failed to create version', 'error');
      }
    } catch (err) {
      console.error('Error creating version:', err);
      showNotification(err.response?.data?.error || 'Failed to create version', 'error');
    }
  }, [showNotification, announceToScreenReader, fetchQuoteDetails]);

  const handleViewVersionHistory = useCallback((lineId) => {
    setSelectedLineForVersion(lineId);
    setShowVersionHistoryModal(true);
    announceToScreenReader(`Viewing version history for line ${lineId}`);
  }, [announceToScreenReader]);

  // ────────────────────────────────────────────────────────────────────────────
  // Bulk Actions Handlers
  // ────────────────────────────────────────────────────────────────────────────

  const handleToggleBulkEditMode = useCallback(() => {
    setBulkEditMode(prev => !prev);
  }, []);

  const handleBulkValuesChange = useCallback((newValues) => {
    setBulkValues(newValues);
  }, []);

  const handleQuickBulkAction = useCallback(async (values) => {
    setBulkValues(values);
    if (!selectedLines.size) return;
    const changes = {};
    Object.entries(values).forEach(([k, v]) => {
      if (v !== '') changes[k] = v;
    });
    if (!Object.keys(changes).length) return;

    setSaving(true);
    try {
      const res = await api.post('/api/quote-lines/bulk-update', {
        line_ids: Array.from(selectedLines),
        changes
      });
      if (res.data.success) {
        setLines(prev =>
          prev.map(l => {
            const updated = res.data.lines.find(u => u.id === l.id);
            return updated ? { ...l, ...updated } : l;
          })
        );
        showNotification(`Updated ${selectedLines.size} lines`, 'success');
        announceToScreenReader(`Bulk update completed for ${selectedLines.size} lines`);
        clearSelection();
      } else {
        showNotification(res.data.error || 'Failed to update lines', 'error');
        announceToScreenReader(res.data.error || 'Bulk update failed');
      }
    } catch (err) {
      console.error('Bulk update error:', err);
      showNotification(err.response?.data?.error || 'Failed to update lines', 'error');
      announceToScreenReader('Bulk update failed');
    } finally {
      setSaving(false);
    }
  }, [selectedLines, showNotification, announceToScreenReader, clearSelection]);

  const applyBulkChanges = useCallback(async () => {
    if (!selectedLines.size) return;
    const changes = {};
    Object.entries(bulkValues).forEach(([k, v]) => {
      if (v !== '') changes[k] = v;
    });
    if (!Object.keys(changes).length) {
      return showNotification('Please select values to apply', 'warning');
    }
    setSaving(true);
    try {
      const res = await api.post('/api/quote-lines/bulk-update', {
        line_ids: Array.from(selectedLines),
        changes
      });
      if (res.data.success) {
        setLines(prev =>
          prev.map(l => {
            const updated = res.data.lines.find(u => u.id === l.id);
            return updated ? { ...l, ...updated } : l;
          })
        );
        showNotification(`Updated ${selectedLines.size} lines`, 'success');
        announceToScreenReader(`Bulk update completed for ${selectedLines.size} lines`);
        clearSelection();
      } else {
        showNotification(res.data.error || 'Failed to update lines', 'error');
        announceToScreenReader(res.data.error || 'Bulk update failed');
      }
    } catch (err) {
      console.error('Bulk update error:', err);
      showNotification(err.response?.data?.error || 'Failed to update lines', 'error');
      announceToScreenReader('Bulk update failed');
    } finally {
      setSaving(false);
    }
  }, [selectedLines, bulkValues, showNotification, announceToScreenReader, clearSelection]);

  // ────────────────────────────────────────────────────────────────────────────
  // Template & Vendor Handlers
  // ────────────────────────────────────────────────────────────────────────────

  const handleCloseTemplates = useCallback(() => {
    setShowTemplates(false);
  }, []);

  const handleVendorSelected = useCallback(async (lineId, vendorData) => {
    setLines(prevLines => prevLines.map(l =>
      l.id === lineId ? { ...l, ...vendorData } : l
    ));

    setEditingCell(null);
    setEditValue('');

    const message = `Vendor set to ${vendorData.vendor_name || vendorData.name}`;
    showNotification(message, 'success');

    try {
      setSaving(true);
      const res = await api.post(`/api/quote-lines/${lineId}`, {
        vendor_id: vendorData.vendor_id,
        vendor_name: vendorData.vendor_name,
        ...(vendorData.price_unit && { price_unit: vendorData.price_unit }),
        ...(vendorData.lead_time && { lead_time: vendorData.lead_time })
      });

      if (res.data.success) {
        setLines(prevLines => prevLines.map(l =>
          l.id === lineId ? { ...l, ...res.data.line } : l
        ));
      } else {
        showNotification('Vendor selected but failed to save to server', 'warning');
        console.warn('Vendor save failed:', res.data.error);
      }
    } catch (err) {
      console.error('Vendor save error:', err);
      showNotification('Vendor selected but failed to save to server', 'warning');
    } finally {
      setSaving(false);
    }

    autoUpdateToInProgress(lineId);
  }, [autoUpdateToInProgress, showNotification]);

  const applyTemplate = useCallback(async (lineId, data) => {
    setSaving(true);
    try {
      const payload = { ...data };
      if (payload.cert_name) {
        payload.certification_id = getCertificationIdByName(certificationOptions, payload.cert_name);
        delete payload.cert_name;
      }

      const res = await api.post(`/api/quote-lines/${lineId}`, payload);
      if (res.data.success) {
        setLines(prev => prev.map(l => l.id === lineId ? { ...l, ...res.data.line } : l));
        showNotification('Template applied', 'success');
        announceToScreenReader('Template applied successfully');
      }
    } catch (err) {
      console.error('Template error:', err);
      announceToScreenReader('Failed to apply template');
    } finally {
      setSaving(false);
      setShowTemplates(false);
    }
  }, [certificationOptions, showNotification, announceToScreenReader]);

  // ────────────────────────────────────────────────────────────────────────────
  // Alternative Parts Handlers
  // ────────────────────────────────────────────────────────────────────────────

  const toggleOriginalExpansion = useCallback((originalId) => {
    setExpandedOriginals(prev => {
      const next = new Set(prev);
      if (next.has(originalId)) {
        next.delete(originalId);
        announceToScreenReader('Collapsed alternatives');
      } else {
        next.add(originalId);
        announceToScreenReader('Expanded alternatives');
      }
      return next;
    });
  }, [announceToScreenReader]);

  const expandAllOriginals = useCallback(() => {
    const { originalToAlternatives } = transformLinesToNested(lines);
    const allOriginalIds = Array.from(originalToAlternatives.keys());
    setExpandedOriginals(new Set(allOriginalIds));
    announceToScreenReader('Expanded all alternatives');
  }, [lines, transformLinesToNested, announceToScreenReader]);

  const collapseAllOriginals = useCallback(() => {
    setExpandedOriginals(new Set());
    announceToScreenReader('Collapsed all alternatives');
  }, [announceToScreenReader]);

  const createAlternativeForLine = useCallback(async (originalLine) => {
    setSaving(true);
    try {
      const res = await api.post(`/api/quote-lines/${originalLine.id}/create-alternative`, {});

      if (res.data.success) {
        const linesRes = await api.get(`/api/quote-requests/${id}/lines`);
        if (linesRes.data.success) {
          setLines(linesRes.data.lines);
          const { hasAlternatives } = transformLinesToNested(linesRes.data.lines);
          setShowAlternativesUI(hasAlternatives);
        }

        setExpandedOriginals(prev => new Set(prev).add(originalLine.id));
        showNotification(res.data.message || 'Alternative created successfully', 'success');
        announceToScreenReader(`Alternative created for ${originalLine.name}`);
      } else {
        showNotification('Failed to create alternative', 'error');
      }
    } catch (err) {
      console.error('Create alternative error:', err);
      showNotification('Error creating alternative: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setSaving(false);
    }
  }, [id, transformLinesToNested, showNotification, announceToScreenReader]);

  // ────────────────────────────────────────────────────────────────────────────
  // Navigation Helpers
  // ────────────────────────────────────────────────────────────────────────────

  const highlightCell = useCallback((lineIndex, field) => {
    document.querySelectorAll('.cell-focused').forEach(el => el.classList.remove('cell-focused'));
    const cell = document.getElementById(`cell-${lineIndex}-${field}`);
    if (cell) {
      cell.classList.add('cell-focused');
      cell.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      cell.focus();
    }
  }, []);

  const navigateCell = useCallback((direction, fields) => {
    if (!focusedCell) return;
    let { lineIndex, field } = focusedCell;
    const idx = fields.indexOf(field);
    let newLine = lineIndex, newFieldIdx = idx;

    switch (direction) {
      case 'right':
      case 'tab':
        newFieldIdx++;
        if (newFieldIdx >= fields.length) { newFieldIdx = 0; newLine++; }
        break;
      case 'left':
      case 'shift-tab':
        newFieldIdx--;
        if (newFieldIdx < 0) { newFieldIdx = fields.length - 1; newLine--; }
        break;
      case 'up':
        newLine--;
        break;
      case 'down':
        newLine++;
        break;
      default:
        break;
    }

    if (newLine < 0 || newLine >= lines.length) return;

    const nf = fields[newFieldIdx];
    const newFocusedCell = { lineIndex: newLine, field: nf };
    setFocusedCell(newFocusedCell);
    highlightCell(newLine, nf);

    const line = lines[newLine];
    const cellDescription = `${getRowDescription(line, newLine)}, ${getFieldLabel(nf)}: ${getCellValue(line, nf)}`;
    announceToScreenReader(cellDescription);
  }, [focusedCell, lines, highlightCell, announceToScreenReader]);

  // ────────────────────────────────────────────────────────────────────────────
  // Editing Handlers
  // ────────────────────────────────────────────────────────────────────────────

  const startEditing = useCallback((lineId, field, currentValue) => {
    const line = lines.find(l => l.id === lineId);
    if (line?.state === 'completed') {
      announceToScreenReader('Cannot edit completed line');
      return;
    }
    if (line?.state === 'draft') autoUpdateToInProgress(lineId);

    let editVal = currentValue ?? '';
    if (field === 'price_unit' || field === 'lead_time' || field === 'product_qty') {
      // Defensive: seed numeric editors with a raw number even if the backend
      // (or a formatted display string) hands us "$4,200.00"-style text.
      // A legitimate 0 must still seed as 0, not fall back to ''.
      if (editVal !== '') {
        const numeric = parseFloat(String(editVal).replace(/[^0-9.-]/g, ''));
        editVal = Number.isNaN(numeric) ? '' : numeric;
      }
    } else if (field === 'currency_id' || field === 'condition' || field === 'certification_id') {
      editVal = String(currentValue ?? '');
    }

    console.log(`startEditing - field: ${field}, lineId: ${lineId}, currentValue:`, currentValue, 'editVal:', editVal, 'line:', line);

    setEditingCell({ lineId, field });
    setEditValue(editVal);
    setIsDirty(false);

    setValidationErrors(prev => {
      const copy = { ...prev };
      delete copy[`${lineId}_${field}`];
      return copy;
    });

    announceToScreenReader(`Editing ${getFieldLabel(field)} for part ${line?.name}. Current value: ${currentValue || 'empty'}`);

    setTimeout(() => {
      const el = editInputRef.current;
      if (el) {
        el.focus();
        if (typeof el.select === 'function') el.select();
      }
    }, 50);
  }, [lines, autoUpdateToInProgress, announceToScreenReader]);

  const handleCellClick = useCallback((lineId, field, currentValue, lineIndex) => {
    if (field === 'vendor_id') {
      const line = lines.find(l => l.id === lineId);
      if (line?.state !== 'completed') {
        startEditing(lineId, 'vendor_id', line.vendor_name || '');
      } else {
        setFocusedCell({ lineIndex, field: 'vendor_id' });
      }
    } else {
      const line = lines.find(l => l.id === lineId);
      if (line?.state !== 'completed') {
        startEditing(lineId, field, currentValue);
      } else {
        setFocusedCell({ lineIndex, field });
      }
    }
  }, [lines, startEditing]);

  const handleCellKeyDown = useCallback((lineId, field, currentValue) => {
    startEditing(lineId, field, currentValue);
  }, [startEditing]);

  const handleEditChange = useCallback((e) => {
    const v = e.target.value;
    setEditValue(v);
    setIsDirty(true);
    if (editingCell) {
      const err = validateField(editingCell.field, v);
      setValidationErrors(prev => ({ ...prev, [`${editingCell.lineId}_${editingCell.field}`]: err }));
      if (err) announceToScreenReader(`Validation error: ${err}`);
    }
  }, [editingCell, validateField, announceToScreenReader]);

  const saveEditWithValue = useCallback(async (lineId, field, value) => {
    const line = lines.find(l => l.id === lineId);
    if (!line) return cancelEditing();

    let newVal = value;
    if (field === 'price_unit') {
      newVal = parseFloat(String(value).replace(/,/g, ''));
    } else if (field === 'lead_time') {
      newVal = parseInt(value);
    } else if (field === 'currency_id' || field === 'certification_id') {
      newVal = parseInt(value, 10);
    }

    const valErr = validateField(field, newVal);
    if (valErr) {
      setValidationErrors(prev => ({ ...prev, [`${lineId}_${field}`]: valErr }));
      announceToScreenReader(`Cannot save: ${valErr}`);
      return;
    }

    setSaving(true);
    try {
      const res = await api.post(`/api/quote-lines/${lineId}`, { [field]: newVal });
      if (res.data.success) {
        const vendorPreservation = line.vendor_id ? {
          vendor_id: line.vendor_id,
          vendor_name: line.vendor_name
        } : {};

        setLines(prev => prev.map(l => l.id === lineId ? {
          ...l,
          ...res.data.line,
          ...vendorPreservation
        } : l));

        const message = `${getFieldLabel(field)} saved as ${newVal} for part ${line.name}`;
        if (res.data.message) showNotification(res.data.message, 'info');
        announceToScreenReader(message);

        if (field === 'condition') autoApplyCertification(lineId, newVal);
      } else {
        showNotification('Failed to save changes', 'error');
        announceToScreenReader('Failed to save changes');
      }
    } catch (err) {
      console.error('Save edit error:', err);
      showNotification('Error saving changes', 'error');
      announceToScreenReader('Error saving changes');
    } finally {
      setSaving(false);
      setEditingCell(null);
      setIsDirty(false);
    }
  }, [lines, cancelEditing, validateField, showNotification, announceToScreenReader, autoApplyCertification]);

  const handleDropdownChange = useCallback((e) => {
    const v = e.target.value;
    setEditValue(v);
    setIsDirty(true);

    if (editingCell && ['condition', 'certification_id', 'currency_id', 'product_uom_id'].includes(editingCell.field)) {
      const valueToValidate = (editingCell.field === 'currency_id' || editingCell.field === 'certification_id' || editingCell.field === 'product_uom_id')
        ? parseInt(v, 10)
        : v;

      const err = validateField(editingCell.field, valueToValidate);
      if (err) {
        setValidationErrors(prev => ({ ...prev, [`${editingCell.lineId}_${editingCell.field}`]: err }));
        announceToScreenReader(`Validation error: ${err}`);
      } else {
        setValidationErrors(prev => {
          const copy = { ...prev };
          delete copy[`${editingCell.lineId}_${editingCell.field}`];
          return copy;
        });

        setTimeout(() => saveEditWithValue(editingCell.lineId, editingCell.field, valueToValidate), 50);
      }
    }
  }, [editingCell, validateField, announceToScreenReader, saveEditWithValue]);

  const saveEdit = useCallback(async (lineId, field) => {
    const line = lines.find(l => l.id === lineId);
    if (!line) return cancelEditing();

    let newVal = editValue;
    if (field === 'price_unit') {
      newVal = parseFloat(String(editValue).replace(/,/g, ''));
      const originalVal = parseFloat(line.price_unit);
      if (newVal === originalVal) return cancelEditing();
      // Empty/invalid input on a field that was already empty or zero is not
      // a real change - cancel quietly instead of surfacing a validation error.
      if (isNaN(newVal) && (isNaN(originalVal) || originalVal === 0)) {
        return cancelEditing();
      }
    } else if (field === 'lead_time') {
      newVal = parseInt(editValue);
      const originalVal = parseInt(line.lead_time);
      if (newVal === originalVal) return cancelEditing();
      if (isNaN(newVal) && (isNaN(originalVal) || originalVal === 0)) {
        return cancelEditing();
      }
    } else if (String(newVal) === String(line[field])) {
      return cancelEditing();
    }

    return saveEditWithValue(lineId, field, newVal);
  }, [lines, editValue, cancelEditing, saveEditWithValue]);

  const handleEditBlur = useCallback((lineId, field) => {
    saveEdit(lineId, field);
  }, [saveEdit]);

  // ────────────────────────────────────────────────────────────────────────────
  // Auto-save Effect
  // ────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isDirty || !autoSaveEnabled || !editingCell) return;
    const timer = setTimeout(() => {
      const err = validateField(editingCell.field, editValue);
      if (!err) saveEdit(editingCell.lineId, editingCell.field);
    }, 2000);
    return () => clearTimeout(timer);
  }, [editValue, isDirty, editingCell, autoSaveEnabled, validateField, saveEdit]);

  const handleEditKeyPress = useCallback((e, lineId, field) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        saveEdit(lineId, field);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        cancelEditing();
        break;
      case 'Tab':
        e.preventDefault();
        e.stopPropagation();
        saveEdit(lineId, field).then(() => {
          navigateCell(e.shiftKey ? 'shift-tab' : 'tab',
            ['vendor_id', 'condition', 'certification_id', 'price_unit', 'lead_time']
          );
        });
        break;
      default:
        break;
    }
  }, [saveEdit, cancelEditing, navigateCell]);

  // ────────────────────────────────────────────────────────────────────────────
  // Global Keyboard Navigation
  // ────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (editingCell !== null) {
        if (e.key !== 'Escape') {
          return;
        }
      }

      const tag = (e.target.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.isContentEditable) {
        return;
      }

      if (bulkEditMode) return;

      // Only hijack browser-native keys (Tab focus traversal, Ctrl+A select-all)
      // when the grid is actually engaged, so buttons/links and normal page
      // text selection keep working everywhere else.
      const gridEngaged = focusedCell !== null || (tableRef.current && tableRef.current.contains(e.target));

      const fields = ['vendor_id', 'condition', 'certification_id', 'price_unit', 'lead_time'];

      switch (e.key) {
        case 'Tab':
          if (!gridEngaged) return;
          e.preventDefault();
          if (focusedCell) {
            navigateCell(e.shiftKey ? 'shift-tab' : 'tab', fields);
          } else if (lines.length) {
            setFocusedCell({ lineIndex: 0, field: fields[0] });
            highlightCell(0, fields[0]);
          }
          break;
        case 'ArrowRight':
          if (!editingCell && focusedCell) { e.preventDefault(); navigateCell('right', fields); }
          break;
        case 'ArrowLeft':
          if (!editingCell && focusedCell) { e.preventDefault(); navigateCell('left', fields); }
          break;
        case 'ArrowUp':
          if (!editingCell && focusedCell) { e.preventDefault(); navigateCell('up', fields); }
          break;
        case 'ArrowDown':
          if (!editingCell && focusedCell) { e.preventDefault(); navigateCell('down', fields); }
          break;
        case 'Enter':
          if (focusedCell && !editingCell) {
            e.preventDefault();
            const line = lines[focusedCell.lineIndex];
            if (line && line.state !== 'completed') {
              if (focusedCell.field === 'vendor_id') {
                startEditing(line.id, 'vendor_id', line.vendor_name || '');
              } else {
                startEditing(line.id, focusedCell.field, line[focusedCell.field]);
              }
            }
          }
          break;
        case 'Escape':
          if (editingCell) cancelEditing();
          else if (selectedLines.size) clearSelection();
          break;
        case 'c':
          if (e.ctrlKey && focusedCell && !editingCell) {
            e.preventDefault();
            const ln = lines[focusedCell.lineIndex];
            e.shiftKey ? completeLine(ln.id) : copyLineData(ln);
          }
          break;
        case 'v':
          if (e.ctrlKey && focusedCell && !editingCell) {
            e.preventDefault();
            pasteToLine(lines[focusedCell.lineIndex].id);
          }
          break;
        case 'd':
          if (e.ctrlKey && focusedCell && !editingCell) {
            e.preventDefault();
            duplicateLineData(lines[focusedCell.lineIndex]);
          }
          break;
        case 'F2':
          if (focusedCell && !editingCell && focusedCell.field === 'vendor_id') {
            e.preventDefault();
            startEditing(lines[focusedCell.lineIndex].id, 'vendor_id', lines[focusedCell.lineIndex].vendor_name || '');
          }
          break;
        case 'a':
          if (e.ctrlKey) {
            if (!gridEngaged) return;
            e.preventDefault();
            selectAllLines();
          }
          break;
        case 'f':
          if (!editingCell && focusedCell?.field === 'condition') {
            quickUpdateField(lines[focusedCell.lineIndex].id, 'condition', 'FN');
          }
          break;
        case 'n':
          if (!editingCell && focusedCell?.field === 'condition') {
            quickUpdateField(lines[focusedCell.lineIndex].id, 'condition', 'NE');
          }
          break;
        case 's':
          if (!editingCell && focusedCell?.field === 'condition') {
            quickUpdateField(lines[focusedCell.lineIndex].id, 'condition', 'NS');
          }
          break;
        case 'o':
          if (!editingCell && focusedCell?.field === 'condition') {
            quickUpdateField(lines[focusedCell.lineIndex].id, 'condition', 'OH');
          }
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [
    focusedCell, editingCell, lines, bulkEditMode, selectedLines,
    navigateCell, highlightCell, startEditing, cancelEditing, clearSelection,
    selectAllLines, copyLineData, pasteToLine, completeLine, duplicateLineData, quickUpdateField
  ]);

  // ────────────────────────────────────────────────────────────────────────────
  // Derived Render Data (memoized so it isn't rebuilt on every keystroke)
  // ────────────────────────────────────────────────────────────────────────────

  const { displayLines, originalToAlternatives } = useMemo(
    () => transformLinesToNested(lines),
    [lines, transformLinesToNested]
  );

  const lineIndexById = useMemo(() => {
    const map = new Map();
    lines.forEach((l, idx) => map.set(l.id, idx));
    return map;
  }, [lines]);

  // Pagination is purely a rendering concern: `lines`, `lineIndexById`, and
  // `displayLines` above stay whole-quote so keyboard grid navigation
  // (lineIndex is an index into the flat, unpaginated `lines` array) and the
  // global keyboard-shortcut handlers (which look up `lines[focusedCell.lineIndex]`)
  // keep working unchanged. Only the rows actually rendered in the table body
  // are page-scoped, via `pagedDisplayLines` below.
  const originalDisplayLines = useMemo(
    () => displayLines.filter(l => l.type === 'original'),
    [displayLines]
  );

  const pagination = useMemo(
    () => paginateOriginals(originalDisplayLines, currentPage, pageSize),
    [originalDisplayLines, currentPage, pageSize]
  );

  const pagedDisplayLines = useMemo(() => {
    if (!pagination.paginated) return displayLines;
    return displayLines.filter(l =>
      l.type === 'original' ? pagination.pageIds.has(l.id) : pagination.pageIds.has(l.parentId)
    );
  }, [displayLines, pagination]);

  const goToPage = useCallback((p) => {
    setCurrentPage(Math.min(Math.max(1, p), pagination.totalPages));
  }, [pagination.totalPages]);

  // ────────────────────────────────────────────────────────────────────────────
  // JSX
  // ────────────────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="loading" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <span className="sr-only">Loading quote details...</span>
    </div>
  );

  if (error) return (
    <div className="error" role="alert" aria-live="assertive">
      <p>{error}</p>
      <div className="error-actions">
        <button onClick={fetchQuoteDetails} className="retry-btn">
          Retry
        </button>
        <Link to="/quotes" className="back-to-quotes-link">
          Back to Quotes
        </Link>
      </div>
    </div>
  );

  return (
    <div className="quote-request-detail st">
      {/* Screen reader announcements */}
      <div
        ref={screenReaderRef}
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />


      {/* Skip to content link */}
      <a href="#main-table" className="skip-link">
        Skip to quote lines table
      </a>

      {/* Header Section */}
      <header className="header-section">
        <QuoteHeader request={request} />
        <ProgressSection lines={lines} />

        {/* Quote Actions */}
        {request && (
          <QuoteActions
            quoteRequest={request}
            onActionComplete={(data, actionName) => {
              if (actionName === 'duplicate') {
                showNotification(`Quote duplicated successfully! New quote ID: ${data.new_request?.id}`, 'success');
              } else {
                showNotification(`Quote ${actionName} successfully!`, 'success');
                fetchQuoteDetails();
              }
            }}
            className="quote-actions-section"
          />
        )}

        <ActionToolbar
          lines={pagination.paginated ? pagedDisplayLines : lines}
          selectAllLines={selectAllLines}
          label={pagination.paginated ? 'Select Page' : 'Select All'}
          autoSaveEnabled={autoSaveEnabled}
          setAutoSaveEnabled={setAutoSaveEnabled}
        />
      </header>

      {/* Main Table */}
      <main>
        {/* Alternatives Toolbar */}
        {showAlternativesUI && (
          <div className="alternatives-toolbar">
            <button
              onClick={expandAllOriginals}
              className="expand-all-btn"
              aria-label="Expand all alternatives"
            >
              Expand All Alternatives
            </button>
            <button
              onClick={collapseAllOriginals}
              className="collapse-all-btn"
              aria-label="Collapse all alternatives"
            >
              Collapse All Alternatives
            </button>
          </div>
        )}

        <div className="table-container" ref={tableRef}>
          <table
            style={{ marginBottom: '100px' }}
            id="main-table"
            className="lines-table"
            role="grid"
            aria-label="Quote lines data grid"
            aria-describedby="table-instructions"
          >
            <caption id="table-instructions" className="sr-only">
              Quote lines table. Use arrow keys or tab to navigate cells. Click or press Enter to edit a cell.
              Type in vendor column to search vendors inline. Use Ctrl+C to copy and Ctrl+V to paste line data.
            </caption>

            <thead>
              <tr role="row">
                <th scope="col" style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={
                      selectedLines.size > 0 &&
                      selectedLines.size === (pagination.paginated ? pagedDisplayLines : lines).filter(l => l.state !== 'completed').length
                    }
                    onChange={selectedLines.size ? clearSelection : selectAllLines}
                    aria-label={pagination.paginated ? 'Select all incomplete lines on this page' : 'Select all incomplete lines'}
                  />
                </th>
                <th scope="col" style={{ width: '50px' }} aria-label="Alternative indicator"></th>
                <th scope="col" style={{ width: '60px' }}>Version</th>
                <th scope="col">Part Number</th>
                <th scope="col">Description</th>
                <th scope="col">Qty</th>
                <th scope="col">UoM</th>
                <th scope="col">Vendor</th>
                <th scope="col">Condition</th>
                <th scope="col">Certification</th>
                <th scope="col">Price</th>
                <th scope="col">Currency</th>
                <th scope="col">Lead Time</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>

            <tbody>
              {(() => {
                return pagedDisplayLines.map((line) => {
                  const isOriginal = line.type === 'original';
                  const isAlternative = line.type === 'alternative';
                  const isExpanded = expandedOriginals.has(line.id);
                  const hasAlternatives = isOriginal && originalToAlternatives.has(line.id);
                  const alternativesCount = hasAlternatives ? originalToAlternatives.get(line.id).length : 0;
                  const lineIndex = lineIndexById.get(line.id);

                  return (
                    <React.Fragment key={line.id}>
                      {isOriginal && hasAlternatives && showAlternativesUI && (
                        <tr className="alternative-group-header">
                          <td colSpan="15">
                            <div className="alternative-controls">
                              <button
                                onClick={() => toggleOriginalExpansion(line.id)}
                                className="expand-collapse-btn"
                                aria-label={isExpanded ? 'Collapse alternatives' : 'Expand alternatives'}
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? '▼' : '►'} {alternativesCount} Alternative{alternativesCount !== 1 ? 's' : ''}
                              </button>
                              <button
                                onClick={() => createAlternativeForLine(line)}
                                className="create-alternative-btn"
                                disabled={line.state === 'completed'}
                                aria-label={`Create alternative for ${line.name}`}
                              >
                                + Add Alternative
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      <QuoteLineRow
                        key={line.id}
                        line={line}
                        lineIndex={lineIndex}
                        isAlternative={isAlternative}
                        isFirstAlternative={line.isFirstAlternative || false}
                        isLastAlternative={line.isLastAlternative || false}
                        isOriginalWithAlternatives={hasAlternatives}
                        alternativesCount={alternativesCount}
                        isExpanded={isExpanded}
                        onToggleExpansion={hasAlternatives ? toggleOriginalExpansion : null}
                        onCreateAlternative={isOriginal && !line.is_orphaned_alt ? createAlternativeForLine : null}
                        selectedLines={selectedLines}
                        editingCell={editingCell}
                        editValue={editValue}
                        editInputRef={editInputRef}
                        focusedCell={focusedCell}
                        validationErrors={validationErrors}
                        saving={saving}
                        conditionOptions={conditionOptions}
                        certificationOptions={certificationOptions}
                        currencyOptions={currencyOptions}
                        uomOptions={uomOptions}
                        copiedData={copiedData}
                        onLineSelectionChange={handleLineSelectionChange}
                        onCellClick={handleCellClick}
                        onCellKeyDown={handleCellKeyDown}
                        onEditChange={handleEditChange}
                        onDropdownChange={handleDropdownChange}
                        onEditKeyPress={handleEditKeyPress}
                        onEditBlur={handleEditBlur}
                        onVendorSelected={handleVendorSelected}
                        onVendorCancel={handleVendorCancel}
                        onQuickUpdateField={quickUpdateField}
                        onCopyLineData={copyLineData}
                        onPasteToLine={pasteToLine}
                        onCompleteLine={completeLine}
                        onNoQuoteLine={noQuoteLine}
                        onDuplicateLineData={duplicateLineData}
                        onShowTemplates={handleShowTemplates}
                        onViewLineMessages={handleViewLineMessages}
                        onAddRemarks={handleAddRemarks}
                        onUploadCertificate={handleUploadCertificate}
                        onCreateVersion={handleCreateVersion}
                        onViewVersionHistory={handleViewVersionHistory}
                        getFieldLabel={getFieldLabel}
                        getCellValue={getCellValue}
                        getRowDescription={getRowDescription}
                        announceToScreenReader={announceToScreenReader}
                      />
                    </React.Fragment>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>

        {pagination.paginated && (
            <div className="pagination-bar" role="navigation" aria-label="Quote lines pagination">
              <div className="pagination-info">
                Showing {pagination.startIdx + 1}–{pagination.endIdx} of {pagination.total} lines
              </div>

              <div className="pagination-controls">
                <button
                  type="button"
                  className="pagination-btn pagination-nav-btn"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  aria-label="Previous page"
                >
                  ‹ Prev
                </button>

                {getPageNumbers(pagination.page, pagination.totalPages).map((p, idx) => (
                  p === '…' ? (
                    <span key={`ellipsis-${idx}`} className="pagination-ellipsis" aria-hidden="true">…</span>
                  ) : (
                    <button
                      type="button"
                      key={p}
                      className={`pagination-btn pagination-page-btn ${p === pagination.page ? 'active' : ''}`}
                      onClick={() => goToPage(p)}
                      aria-current={p === pagination.page ? 'page' : undefined}
                      aria-label={`Page ${p}`}
                    >
                      {p}
                    </button>
                  )
                ))}

                <button
                  type="button"
                  className="pagination-btn pagination-nav-btn"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  aria-label="Next page"
                >
                  Next ›
                </button>
              </div>

              <div className="pagination-page-size">
                <label htmlFor="page-size-select">Rows per page:</label>
                <select
                  id="page-size-select"
                  value={pageSize}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPageSize(v === 'all' ? 'all' : parseInt(v, 10));
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt === 'all' ? 'All' : opt}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
      </main>

      {/* Floating Action Buttons */}
      <div className={`floating-buttons ${selectedLines.size > 0 ? 'toolbar-open' : ''}`}>
        <button
          className="fab-button fab-messages"
          onClick={() => setShowMessaging(!showMessaging)}
          aria-expanded={showMessaging}
          aria-controls="messaging-content"
          title="Messages & Notes"
        >
          <span className="fab-icon">💬</span>
          {selectedLineForMessaging && (
            <span className="fab-badge">{selectedLineForMessaging.part_number}</span>
          )}
        </button>
      </div>

      {/* Messaging Panel Modal */}
      {showMessaging && (
        <div className="messaging-modal-overlay" onClick={handleCloseMessaging}>
          <aside
            className="messaging-panel"
            onClick={(e) => e.stopPropagation()}
            ref={messagingPanelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="messaging-panel-title"
          >
            <div className="messaging-header">
              <div className="messaging-header-content">
                <h3 id="messaging-panel-title">
                  {selectedLineForMessaging
                    ? `Messages - Line ${selectedLineForMessaging.part_number}`
                    : 'Messages & Notes'
                  }
                </h3>
                {selectedLineForMessaging && (
                  <button
                    className="clear-line-selection"
                    onClick={() => setSelectedLineForMessaging(null)}
                    title="Show quote request messages"
                  >
                    ✕ Clear Line Filter
                  </button>
                )}
              </div>
              <button
                className="messaging-close"
                onClick={handleCloseMessaging}
                aria-label="Close messages"
              >
                ✕
              </button>
            </div>

            <div id="messaging-content" className="messaging-content">
              {selectedLineForMessaging ? (
                <QuoteMessaging
                  ref={lineMessagingRef}
                  key={`line-${selectedLineForMessaging.id}`}
                  lineId={selectedLineForMessaging.id}
                  autoLoad={true}
                  className="line-messaging"
                />
              ) : (
                <QuoteMessaging
                  ref={requestMessagingRef}
                  key={`request-${id}`}
                  requestId={parseInt(id)}
                  autoLoad={true}
                  className="request-messaging"
                />
              )}
            </div>
          </aside>
        </div>
      )}

      <TemplatesModal
        showTemplates={showTemplates}
        templates={templates}
        onClose={handleCloseTemplates}
        onApplyTemplate={applyTemplate}
        templateTargetLineId={templateTargetLineId}
      />

      <RemarksModal
        line={selectedLineForRemarks}
        isOpen={showRemarksModal}
        onClose={() => setShowRemarksModal(false)}
        onSave={(remarks) => {
          console.log('Remarks saved:', remarks);
          setShowRemarksModal(false);
        }}
      />

      <CertificateModal
        line={selectedLineForCertificate}
        isOpen={showCertificateModal}
        onClose={() => setShowCertificateModal(false)}
        onUpload={(filename) => {
          console.log('Certificate uploaded:', filename);
        }}
      />

      <VersionHistoryModal
        lineId={selectedLineForVersion}
        isOpen={showVersionHistoryModal}
        onClose={() => setShowVersionHistoryModal(false)}
        onVersionCreated={(newVersion) => {
          showNotification(`Version ${newVersion.version_number} created successfully!`, 'success');
          fetchQuoteDetails();
        }}
      />

      <BulkActionsToolbar
        selectedLines={selectedLines}
        bulkEditMode={bulkEditMode}
        bulkValues={bulkValues}
        conditionOptions={conditionOptions}
        certificationOptions={certificationOptions}
        saving={saving}
        onClearSelection={clearSelection}
        onToggleBulkEditMode={handleToggleBulkEditMode}
        onBulkValuesChange={handleBulkValuesChange}
        onApplyBulkChanges={applyBulkChanges}
        onQuickBulkAction={handleQuickBulkAction}
        onBulkNoQuote={bulkNoQuote}
      />

      <NotificationToast notification={notification} />

      {/* Global saving indicator */}
      {saving && (
        <div className="global-loading" role="status" aria-live="polite">
          <span className="sr-only">Saving changes...</span>
          Saving...
        </div>
      )}
    </div>
  );
}
